import {
  CheckCircle2,
  Shield,
  ShieldAlert,
  Users,
  Database,
  Settings,
} from "lucide-react";
import { DataActResult } from "../types";

interface DataActAssessmentFeedbackProps {
  productName: string;
  version: number;
  result: DataActResult;
}

export default function DataActAssessmentFeedback({
  productName,
  version,
  result,
}: DataActAssessmentFeedbackProps) {
  const inScope = result.inScope;

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center py-6">
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
            inScope
              ? "bg-blue-100 dark:bg-blue-900/30"
              : "bg-green-100 dark:bg-green-900/30"
          }`}
        >
          <CheckCircle2
            className={`h-8 w-8 ${inScope ? "text-blue-600 dark:text-blue-400" : "text-green-600 dark:text-green-400"}`}
          />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          Assessment Complete
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {productName} — Version {version}
        </p>
      </div>

      {/* Scope Badge */}
      <div className="flex justify-center">
        <span
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-semibold ${
            inScope
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
              : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
          }`}
        >
          {inScope ? (
            <ShieldAlert className="h-5 w-5" />
          ) : (
            <Shield className="h-5 w-5" />
          )}
          {inScope ? "In Scope" : "Out of Scope"}
        </span>
      </div>

      {!inScope && result.outOfScopeReason && (
        <div className="card p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Reason:{" "}
            </span>
            {result.outOfScopeReason}
          </p>
        </div>
      )}

      {inScope && (
        <>
          {/* Classifications */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Classifications
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ClassificationCard
                label="Connected Product"
                active={result.classifications.connectedProduct}
              />
              <ClassificationCard
                label="Related Service"
                active={result.classifications.relatedService}
              />
              <ClassificationCard
                label="Data Processing Service"
                active={result.classifications.dataProcessingService}
              />
            </div>
          </div>

          {/* Roles */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Roles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ClassificationCard
                label="Manufacturer"
                active={result.roles.manufacturer}
              />
              {result.roles.dataHolder !== undefined && (
                <ClassificationCard
                  label="Data Holder"
                  active={result.roles.dataHolder}
                />
              )}
              <ClassificationCard
                label="Related Service Provider"
                active={result.roles.relatedServiceProvider}
              />
              <ClassificationCard label="User" active={result.roles.user} />
            </div>
          </div>

          {/* Data Categories */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Database className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Data Categories
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Product Data
                </span>
                <BoolBadge value={result.dataCategories.productData} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Related Service Data
                </span>
                <BoolBadge value={result.dataCategories.relatedServiceData} />
              </div>
              {result.dataCategories.enrichmentLevel && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Enrichment Level
                  </span>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {result.dataCategories.enrichmentLevel}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Properties */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Settings className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Properties
            </h3>
            <div className="space-y-3">
              <PropertyRow
                label="Readily Available"
                value={result.properties.readilyAvailable}
                positive
              />
              <PropertyRow
                label="Contains Trade Secrets"
                value={result.properties.tradeSecrets}
              />
              <PropertyRow
                label="Safety Risk"
                value={result.properties.safetyRisk}
              />
              <PropertyRow
                label="Contains Personal Data"
                value={result.properties.personalData}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ClassificationCard({
  label,
  active,
}: {
  label: string;
  active: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 text-center ${
        active
          ? "border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20"
          : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
      }`}
    >
      <span
        className={`text-sm font-medium ${
          active
            ? "text-primary-800 dark:text-primary-200"
            : "text-gray-400 dark:text-gray-500"
        }`}
      >
        {label}
      </span>
      <div className="mt-1">
        <BoolBadge value={active} />
      </div>
    </div>
  );
}

function BoolBadge({ value }: { value: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        value
          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
      }`}
    >
      {value ? "Yes" : "No"}
    </span>
  );
}

function PropertyRow({
  label,
  value,
  positive = false,
}: {
  label: string;
  value: boolean;
  positive?: boolean;
}) {
  const isGood = positive ? value : !value;
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
          isGood
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        }`}
      >
        {value ? "Yes" : "No"}
      </span>
    </div>
  );
}
